import React, { useCallback } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@mui/material';
import {
  MARGINS_NAME_COLUMN_WIDTH,
  marginsActionsColumnSx,
  marginsPayRangeColumnSx,
  marginsStandardColumnWidth,
} from './marginsTableLayout';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import { TeamMemberViewHeadProps } from './types';
import {
  accountSlice,
  getCalculatingMarginLoading,
  getSortBy,
  getSortOrder,
  setEditablePingId,
  SortAccountMembersBy,
} from '../../redux/account';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { AbsoluteTableSortLabel } from './components/styledComponents/AbsoluteTableSortLabel';
import { SortOrder } from '../../types';

const columns = [
  'Gross',
  'Net',
  'Pay rate',
  'Pay range',
  'Bill rate',
  'Standard bill rate',
  // 'Ratecard',
  'Allocation',
];

const sortableColumns: Record<string, SortAccountMembersBy> = {
  'Team member': SortAccountMembersBy.Name,
  'Pay rate': SortAccountMembersBy.PayRate,
  Gross: SortAccountMembersBy.Margin,
  Net: SortAccountMembersBy.NetMargin,
  'Bill rate': SortAccountMembersBy.BillRate,
  Seniority: SortAccountMembersBy.Seniority,
};

export const TeamMemberViewHead = ({}: TeamMemberViewHeadProps) => {
  const dispatch = useAppDispatch();
  const sortBy = useAppSelector(getSortBy);
  const sortOrder = useAppSelector(getSortOrder);
  const calculatingMarginLoading = useAppSelector(getCalculatingMarginLoading);

  const handleSetEditablePingId = useCallback(() => {
    dispatch(setEditablePingId());
  }, [dispatch]);

  const handleColumnSort = useCallback(
    (columnName: string) => {
      const newSortBy = sortableColumns[columnName];

      if (newSortBy) {
        const newSortOrder =
          newSortBy === sortBy ? 1 - sortOrder : SortOrder.Ascending;
        dispatch(
          accountSlice.actions.setSortBy({
            sortBy: newSortBy,
            sortOrder: newSortOrder,
          }),
        );
      }
    },
    [dispatch, sortOrder, sortBy],
  );

  const firstColumnName = 'Team member';

  const isSortableColumn = (name: SortAccountMembersBy) =>
    Object.values(sortableColumns).includes(name);

  return (
    <TableHead>
      <TableRow>
        <TableCell
          sx={{
            width: '100%',
          }}
        >
          <AbsoluteTableSortLabel
            direction={sortOrder === SortOrder.Ascending ? 'asc' : 'desc'}
            active={sortBy === SortAccountMembersBy.Name}
            onClick={() => handleColumnSort(firstColumnName)}
          >
            <Typography variant="h4" component="p">
              {firstColumnName}
            </Typography>
          </AbsoluteTableSortLabel>
        </TableCell>
        {columns.map((columnName: string) => (
          <TableCell
            sx={(theme: Theme) =>
              columnName === 'Pay range'
                ? marginsPayRangeColumnSx(theme)
                : { width: marginsStandardColumnWidth(theme) }
            }
            key={columnName}
            variant="head"
            align="center"
          >
            {!isSortableColumn(columnName as SortAccountMembersBy) ? (
              <Typography
                textAlign="center"
                whiteSpace="nowrap"
                sx={{ cursor: 'default' }}
                variant="h4"
                component="p"
              >
                {columnName}
              </Typography>
            ) : (
              <AbsoluteTableSortLabel
                direction={sortOrder === SortOrder.Ascending ? 'asc' : 'desc'}
                active={sortBy === sortableColumns[columnName]}
                onClick={() => handleColumnSort(columnName)}
                sx={{ mr: 0 }}
              >
                <Typography whiteSpace="nowrap" variant="h4" component="p">
                  {columnName}
                </Typography>
              </AbsoluteTableSortLabel>
            )}
          </TableCell>
        ))}
        <TableCell sx={(theme: Theme) => marginsActionsColumnSx(theme)}>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            gap={0.5}
          >
            {calculatingMarginLoading ? (
              <CircularProgress size={18} />
            ) : (
              <IconButton
                size="small"
                onClick={handleSetEditablePingId}
                sx={{ p: 0.25 }}
              >
                <HourglassEmptyRoundedIcon
                  color="disabled"
                  sx={{ fontSize: 18 }}
                />
              </IconButton>
            )}
            <Typography
              color="text.disabled"
              fontSize={11}
              sx={{ lineHeight: 1, whiteSpace: 'nowrap' }}
              component="span"
            >
              Hourly
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
    </TableHead>
  );
};
